import { sponsor } from "@/features/sponsor";
import type { EcommerceProduct } from "@/shared";
import {
  getEcommerceItem,
  trackLeanAnalytics as trackWishlistAnalytics,
} from "@/shared";
import {
  addToWishlistEvent,
  pageViewEvent,
  removeFromWishlistEvent,
  selectItemEvent,
  viewItemListEvent,
} from "@dafiti/analytics-sdk";

import type { WishlistProduct } from "../types/wishlist.type";

export const WISHLIST_RECOMMENDED_PRODUCTS_LIST_NAME =
  "wishlist page - recommended products";
export const WISHLIST_PAGE_TITLE = "ecommerce - wishlist";
export const WISHLIST_PAGE_PATH = "/meus-favoritos";
export const WISHLIST_LIST_NAME = "wishlist - my favorites";

type EcommerceParams = Parameters<typeof viewItemListEvent>[0];

type WishlistTrackingProduct = WishlistProduct & { index?: number };

const getWishlistEcommerceItem = (
  product: WishlistTrackingProduct,
  index: number,
) =>
  getEcommerceItem({
    product: product as unknown as Parameters<
      typeof getEcommerceItem
    >[0]["product"],
    listName: WISHLIST_LIST_NAME,
    index,
    isSponsored:
      sponsor.isSponsoredAdsEnabled() &&
      (product.sponsor?.is_sponsored ?? false),
  });

export const wishlistPageView = () => {
  pageViewEvent({
    page_location: window.location.href,
    page_path: WISHLIST_PAGE_PATH,
    page_title: WISHLIST_PAGE_TITLE,
  });

  trackWishlistAnalytics("page_view", {
    page_path: WISHLIST_PAGE_PATH,
    page_title: WISHLIST_PAGE_TITLE,
  });
};

export const viewWishlistItemList = (products: WishlistTrackingProduct[]) => {
  sponsor.trackProductImpression(products);
  viewItemListEvent({
    ecommerce: {
      items: products.map((product, i) =>
        getWishlistEcommerceItem(product, product.index ?? i + 1),
      ),
    } as unknown as EcommerceParams["ecommerce"],
  });

  trackWishlistAnalytics("view_item_list", {
    item_list_name: WISHLIST_LIST_NAME,
    items_quantity: products.length,
  });
};

export const wishlistProductClick = (
  product: WishlistTrackingProduct,
  index: number,
) => {
  sponsor.trackProductClick(product);
  selectItemEvent({
    ecommerce: {
      items: [getWishlistEcommerceItem(product, index)],
    } as unknown as EcommerceParams["ecommerce"],
  });

  trackWishlistAnalytics("select_item", {
    item_id: product.sku,
    item_list_name: WISHLIST_LIST_NAME,
    index,
  });
};

const getEcommerceParams = ({
  product,
  skuSimple,
}: {
  product: EcommerceProduct;
  skuSimple?: string;
}) => ({
  currency: product.price.currency_code,
  value: product.price.value,
  items: [getEcommerceItem({ product, skuSimple })],
});

export const addToWishlistClick = ({
  product,
  skuSimple,
}: {
  product: EcommerceProduct;
  skuSimple?: string;
}) => {
  addToWishlistEvent({
    ecommerce: getEcommerceParams({ product, skuSimple }),
  });

  trackWishlistAnalytics("add_to_wishlist", { sku: product.sku });
};

export const removeFromWishlistClick = ({
  product,
  skuSimple,
}: {
  product: EcommerceProduct;
  skuSimple?: string;
}) => {
  removeFromWishlistEvent({
    ecommerce: getEcommerceParams({ product, skuSimple }),
  });

  trackWishlistAnalytics("remove_from_wishlist", { sku: product.sku });
};
